import { useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { anomalieAdresseElectronique } from '@authsemply/shared';
import { ApiError } from '../../lib/api';
import { CreerEspace } from './CreerEspace';
import { dossiers, facturation, type ProfilFacturation } from '../../lib/dossiers';

/** Ce qu'un dossier peut proposer pour pré-remplir le profil (sa propre identité). */
export interface SuggestionFacturation {
  type?: 'PRO' | 'PARTICULIER';
  name?: string;
  siret?: string;
  vatNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  postCode?: string;
  city?: string;
  country?: string;
  email?: string;
}

interface Props {
  spaceId: string;
  profil: ProfilFacturation | null;
  /** Propriétaire ou administrateur de l'espace : lui seul modifie. */
  modifiable: boolean;
  /** L'identité du dossier en cours de création, proposée comme société facturée. */
  suggestion?: SuggestionFacturation | null;
  onEnregistre: (profil: ProfilFacturation) => void;
  /**
   * Propose « Facturer une autre société ? Créer un espace » (29/10/2026) :
   * l'espace créé, on y commence un dossier.
   */
  autreSociete?: boolean;
}

type Saisie = Omit<ProfilFacturation, 'siren' | 'updatedAt'>;

const VIDE: Saisie = {
  type: 'PRO',
  name: '',
  firstName: null,
  lastName: null,
  siret: null,
  vatNumber: null,
  addressLine1: '',
  addressLine2: null,
  postCode: '',
  city: '',
  country: 'FR',
  email: '',
  adresseElectroniqueScheme: null,
  adresseElectroniqueValeur: null,
  emailsCopie: [],
};

/** Types d'adresse électronique proposés, du plus courant au plus rare. */
const TYPES_ADRESSE = [
  { code: '0225', libelle: 'SIREN, éventuellement suivi d’un suffixe (0225)' },
  { code: '0009', libelle: 'SIRET (0009)' },
  { code: '0002', libelle: 'SIREN seul (0002)' },
  { code: 'EM', libelle: 'Adresse courriel (EM)' },
] as const;

const depuis = (p: ProfilFacturation): Saisie => ({
  type: p.type,
  name: p.name,
  firstName: p.firstName,
  lastName: p.lastName,
  siret: p.siret,
  vatNumber: p.vatNumber,
  addressLine1: p.addressLine1,
  addressLine2: p.addressLine2,
  postCode: p.postCode,
  city: p.city,
  country: p.country,
  email: p.email,
  adresseElectroniqueScheme: p.adresseElectroniqueScheme ?? null,
  adresseElectroniqueValeur: p.adresseElectroniqueValeur ?? null,
  emailsCopie: p.emailsCopie ?? [],
});

/**
 * « Facturé à » (29/10/2026) : la société, ou la personne, au nom de qui
 * l'espace paie. Un espace = un payeur = une série de factures.
 *
 * Exigé avant le premier paiement. Pré-rempli depuis le dossier en cours de
 * création quand c'est la même société, ou par recherche SIRET. Modifier ne
 * touche que les factures suivantes ; changer de société (autre SIREN) se
 * confirme explicitement.
 */
export function BlocFacturation({ spaceId, profil, modifiable, suggestion, onEnregistre, autreSociete = false }: Props) {
  const navigate = useNavigate();
  const [nouvelEspace, setNouvelEspace] = useState(false);
  const [edition, setEdition] = useState(!profil);
  const [saisie, setSaisie] = useState<Saisie>(profil ? depuis(profil) : VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [recherche, setRecherche] = useState<string | null>(null);
  // Les adresses en copie se saisissent une par ligne (ou séparées par des virgules).
  const [copies, setCopies] = useState((profil?.emailsCopie ?? []).join('\n'));

  useEffect(() => {
    if (profil) {
      setSaisie(depuis(profil));
      setCopies((profil.emailsCopie ?? []).join('\n'));
      setEdition(false);
    }
  }, [profil]);

  const champ = (cle: keyof Saisie) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setSaisie((s) => ({ ...s, [cle]: e.target.value }));

  const reprendreDossier = () => {
    if (!suggestion) return;
    setSaisie((s) => ({
      ...s,
      type: suggestion.type ?? s.type,
      name: suggestion.name ?? s.name,
      siret: suggestion.siret ?? s.siret,
      vatNumber: suggestion.vatNumber ?? s.vatNumber,
      addressLine1: suggestion.addressLine1 ?? s.addressLine1,
      addressLine2: suggestion.addressLine2 ?? s.addressLine2,
      postCode: suggestion.postCode ?? s.postCode,
      city: suggestion.city ?? s.city,
      country: suggestion.country ?? s.country,
      email: suggestion.email ?? s.email,
    }));
  };

  const chercherSiret = async () => {
    const siret = (saisie.siret ?? '').replace(/[\s.]/g, '');
    if (!/^\d{14}$/.test(siret)) {
      setRecherche('Saisissez un SIRET de 14 chiffres.');
      return;
    }
    setRecherche('Recherche…');
    try {
      const f = await dossiers.etablissement(siret);
      const voie = [f.numeroVoie, f.indiceRepetition, f.typeVoie, f.libelleVoie].filter(Boolean).join(' ');
      setSaisie((s) => ({
        ...s,
        siret,
        name: f.denomination ?? s.name,
        addressLine1: voie || f.adresse || s.addressLine1,
        addressLine2: f.complementAdresse ?? s.addressLine2,
        postCode: f.codePostal ?? s.postCode,
        city: f.ville ?? s.city,
      }));
      setRecherche(f.actif ? 'Pré-rempli depuis l’annuaire : vérifiez avant d’enregistrer.' : 'Attention : cet établissement est fermé.');
    } catch (e) {
      setRecherche(e instanceof ApiError ? e.message : 'Recherche impossible pour le moment.');
    }
  };

  const adresseValeur = (saisie.adresseElectroniqueValeur ?? '').trim() || null;
  const anomalieAdresse =
    saisie.type === 'PRO' ? anomalieAdresseElectronique(saisie.adresseElectroniqueScheme ?? '0225', adresseValeur) : null;

  const enregistrer = async (confirmer = false) => {
    if (anomalieAdresse) {
      setErreur(anomalieAdresse);
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      const pro = saisie.type === 'PRO';
      const r = await facturation.enregistrer(
        spaceId,
        {
          ...saisie,
          firstName: pro ? null : saisie.firstName,
          lastName: pro ? null : saisie.lastName,
          siret: pro ? (saisie.siret ?? '').replace(/[\s.]/g, '') || null : null,
          vatNumber: pro ? saisie.vatNumber : null,
          adresseElectroniqueScheme: pro && adresseValeur ? saisie.adresseElectroniqueScheme ?? '0225' : null,
          adresseElectroniqueValeur: pro ? adresseValeur : null,
          emailsCopie: [
            ...new Set(
              copies
                .split(/[\s,;]+/)
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean),
            ),
          ],
        },
        confirmer,
      );
      setConfirmation(null);
      setEdition(false);
      onEnregistre(r.profil);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CONFIRMER_CHANGEMENT_SIREN') setConfirmation(e.message);
      else setErreur(e instanceof ApiError ? e.message : 'Le profil de facturation n’a pas pu être enregistré.');
    } finally {
      setEnvoi(false);
    }
  };

  if (profil && !edition) {
    return (
      <section className="dos-recap" aria-labelledby="dos-facture-a">
        <h3 id="dos-facture-a">Facturé à</h3>
        <p>
          <strong>{profil.name}</strong>
          {profil.siren ? ` — SIREN ${profil.siren}` : ''}
          <br />
          {profil.addressLine1}
          {profil.addressLine2 ? `, ${profil.addressLine2}` : ''}, {profil.postCode} {profil.city}
          <br />
          Factures envoyées à {[profil.email, ...(profil.emailsCopie ?? [])].join(', ')}
          {profil.adresseElectroniqueValeur ? (
            <>
              <br />
              Adresse de facturation électronique : {profil.adresseElectroniqueValeur}
              {profil.adresseElectroniqueScheme ? ` (${profil.adresseElectroniqueScheme})` : ''}
            </>
          ) : null}
        </p>
        {modifiable ? (
          <div className="dos-actions">
            <button type="button" className="btn btn-secondaire" onClick={() => setEdition(true)}>
              Modifier
            </button>
          </div>
        ) : null}
        <p className="dos-aide">Une modification ne s’applique qu’aux factures suivantes.</p>
        {autreSociete ? (
          nouvelEspace ? (
            <CreerEspace
              onCree={(e) => navigate(`/dossiers/nouveau?espace=${encodeURIComponent(e.id)}`)}
              onAnnuler={() => setNouvelEspace(false)}
            />
          ) : (
            <p className="dos-aide">
              <button type="button" className="btn-lien" onClick={() => setNouvelEspace(true)}>
                Facturer une autre société ? Créer un espace
              </button>
            </p>
          )
        ) : null}
      </section>
    );
  }

  if (!modifiable) {
    return (
      <p className="dos-aide" role="status">
        Aucune société à facturer n’est encore indiquée pour cet espace : demandez à son propriétaire ou à un administrateur de la renseigner.
      </p>
    );
  }

  const pro = saisie.type === 'PRO';
  return (
    <section className="dos-recap" aria-labelledby="dos-facture-a">
      <h3 id="dos-facture-a">{profil ? 'Modifier la société facturée' : 'À qui facturer ?'}</h3>
      <p className="dos-aide">
        Les factures de votre abonnement seront établies à ce nom. Un espace a un seul payeur : pour facturer une autre
        société séparément, il faut un autre espace.
      </p>

      {suggestion?.name && !profil ? (
        <div className="dos-actions">
          <button type="button" className="btn btn-secondaire" onClick={reprendreDossier}>
            Facturer {suggestion.name} (la société de ce dossier)
          </button>
        </div>
      ) : null}

      <div className="dos-question">
        <label>Type</label>
        <div className="dos-choix">
          {(['PRO', 'PARTICULIER'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`dos-pastille${saisie.type === t ? ' is-actif' : ''}`}
              aria-pressed={saisie.type === t}
              onClick={() => setSaisie((s) => ({ ...s, type: t }))}
            >
              {t === 'PRO' ? 'Une entreprise' : 'Un particulier'}
            </button>
          ))}
        </div>
      </div>

      {pro ? (
        <div className="dos-question">
          <label htmlFor="fac-siret">SIRET (14 chiffres)</label>
          <div className="dos-saisie-bouton">
            <input id="fac-siret" value={saisie.siret ?? ''} inputMode="numeric" maxLength={17} onChange={champ('siret')} />
            <button type="button" className="btn btn-secondaire" onClick={() => void chercherSiret()}>
              Rechercher
            </button>
          </div>
          {recherche ? <p className="dos-aide" role="status">{recherche}</p> : null}
        </div>
      ) : null}

      <div className="dos-question">
        <label htmlFor="fac-nom">{pro ? 'Dénomination' : 'Nom complet'}</label>
        <input id="fac-nom" value={saisie.name} maxLength={200} onChange={champ('name')} />
      </div>
      {pro ? (
        <div className="dos-question">
          <label htmlFor="fac-tva">N° de TVA intracommunautaire (facultatif)</label>
          <input id="fac-tva" value={saisie.vatNumber ?? ''} maxLength={20} onChange={champ('vatNumber')} />
        </div>
      ) : null}
      <div className="dos-question">
        <label htmlFor="fac-adr1">Adresse</label>
        <input id="fac-adr1" value={saisie.addressLine1} maxLength={255} onChange={champ('addressLine1')} />
      </div>
      <div className="dos-question">
        <label htmlFor="fac-adr2">Complément d’adresse (facultatif)</label>
        <input id="fac-adr2" value={saisie.addressLine2 ?? ''} maxLength={255} onChange={champ('addressLine2')} />
      </div>
      <div className="dos-question dos-question--duo">
        <div>
          <label htmlFor="fac-cp">Code postal</label>
          <input id="fac-cp" value={saisie.postCode} maxLength={10} inputMode="numeric" onChange={champ('postCode')} />
        </div>
        <div>
          <label htmlFor="fac-ville">Ville</label>
          <input id="fac-ville" value={saisie.city} maxLength={120} onChange={champ('city')} />
        </div>
      </div>
      <div className="dos-question">
        <label htmlFor="fac-email">E-mail de facturation</label>
        <input id="fac-email" type="email" value={saisie.email} maxLength={255} onChange={champ('email')} />
      </div>
      <div className="dos-question">
        <label htmlFor="fac-copies">Autres destinataires (facultatif, une adresse par ligne, cinq au plus)</label>
        <textarea id="fac-copies" rows={3} value={copies} onChange={(e) => setCopies(e.target.value)} />
        <p className="dos-aide">Ils reçoivent aussi l’avis de chaque facture. La facture reste établie au nom ci-dessus.</p>
      </div>
      {pro ? (
        <div className="dos-question">
          <label htmlFor="fac-adresse-electronique">Adresse de facturation électronique (facultatif)</label>
          <p className="dos-aide" id="fac-adresse-aide">
            L’adresse sous laquelle votre entreprise reçoit ses factures sur sa plateforme agréée, telle qu’inscrite à
            l’annuaire. Sans elle, nos factures vous sont adressées par votre SIREN.
          </p>
          <div className="dos-saisie-bouton">
            <select
              aria-label="Type d’adresse électronique"
              value={saisie.adresseElectroniqueScheme ?? '0225'}
              onChange={champ('adresseElectroniqueScheme')}
            >
              {TYPES_ADRESSE.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.libelle}
                </option>
              ))}
            </select>
            <input
              id="fac-adresse-electronique"
              value={saisie.adresseElectroniqueValeur ?? ''}
              maxLength={255}
              placeholder={(saisie.siret ?? '').replace(/\D/g, '').slice(0, 9) || '123456789'}
              aria-describedby="fac-adresse-aide"
              aria-invalid={Boolean(anomalieAdresse)}
              onChange={champ('adresseElectroniqueValeur')}
            />
          </div>
          {anomalieAdresse ? <p className="dos-erreur">{anomalieAdresse}</p> : null}
        </div>
      ) : null}

      {erreur ? (
        <p className="dos-erreur" role="alert">
          {erreur}
        </p>
      ) : null}

      {confirmation ? (
        <div className="dos-recap" role="alertdialog" aria-labelledby="fac-confirmation">
          <p id="fac-confirmation">{confirmation}</p>
          <div className="dos-actions">
            <button type="button" className="btn btn-secondaire" onClick={() => setConfirmation(null)}>
              Annuler
            </button>
            <button type="button" className="btn" disabled={envoi} onClick={() => void enregistrer(true)}>
              Confirmer le changement de société
            </button>
          </div>
        </div>
      ) : (
        <div className="dos-actions">
          {profil ? (
            <button type="button" className="btn btn-secondaire" onClick={() => setEdition(false)}>
              Annuler
            </button>
          ) : null}
          <button type="button" className="btn" disabled={envoi} onClick={() => void enregistrer()}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}
    </section>
  );
}
